import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { FocusHeader } from '../components/FocusHeader';
import { StepProgressIndicator, ProgressStep } from '../components/StepProgressIndicator';
import { getBlogUrlConfigForUser, generateBlogUrl, generateWidgetBlogUrl } from '../lib/blogUrlHelper';
import { AlertCircle } from 'lucide-react';
import { logGenerationError } from '../lib/errorLogger';
import { generateBlogTeaserHtml, resolveEmailImageUrl } from '../lib/blogTeaserHelper';
import { parseEmailVariant, isUsableEmailVariant, EmailVariantResult } from '../lib/parseEmailVariant';

const CAMPAIGN_GENERATION_TIMEOUT = 60000;
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 2000;

interface FetchWithTimeoutOptions {
  timeout: number;
  signal?: AbortSignal;
}

async function fetchWithTimeout(
  url: string,
  options: RequestInit & FetchWithTimeoutOptions
): Promise<Response> {
  // signal wordt bewust uit fetchOptions gehaald; de eigen AbortController
  // hieronder levert het signal voor de timeout.
  const { timeout, signal: _signal, ...fetchOptions } = options;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Timeout: Het duurt te lang, probeer opnieuw');
    }
    throw error;
  }
}

async function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export default function CampaignMultiBlogGenerate() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const blogIds = (location.state?.blogIds as string[] | undefined) || [];

  const [error, setError] = useState<string | null>(null);
  const [errorCategory, setErrorCategory] = useState<string | null>(null);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [progressSteps, setProgressSteps] = useState<ProgressStep[]>([
    { label: 'Blogs ophalen', status: 'pending' },
    { label: 'Voorwoord voorbereiden', status: 'pending' },
    { label: 'AI voorwoord genereren', status: 'pending' },
    { label: 'Campaign opslaan', status: 'pending' },
  ]);

  const updateStepStatus = (stepIndex: number, status: 'pending' | 'in_progress' | 'completed' | 'error') => {
    setProgressSteps(prev => prev.map((step, idx) =>
      idx === stepIndex ? { ...step, status } : step
    ));
  };

  useEffect(() => {
    if (!user || blogIds.length < 2) {
      navigate('/campaigns');
      return;
    }

    generateCampaign();
  }, [user]);

  const generateCampaign = async () => {
    if (!user || blogIds.length < 2) return;

    try {
      setError(null);

      updateStepStatus(0, 'in_progress');

      const { data: blogsData, error: blogsError } = await supabase
        .from('published_blogs')
        .select('id, slug, title, intro, content, featured_image_url, wp_post_url')
        .in('id', blogIds)
        .eq('user_id', user.id);

      if (blogsError) {
        updateStepStatus(0, 'error');
        throw blogsError;
      }

      // Volgorde van selectie aanhouden; .in() geeft geen gegarandeerde volgorde.
      const blogs = blogIds
        .map((id) => blogsData?.find((b) => b.id === id))
        .filter((b): b is NonNullable<typeof b> => !!b);

      if (blogs.length < 2) {
        updateStepStatus(0, 'error');
        throw new Error('Niet alle geselecteerde blogs konden worden geladen');
      }

      updateStepStatus(0, 'completed');
      updateStepStatus(1, 'in_progress');

      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('first_name, company_name, username_slug, sendgrid_from_name, website_url, email_greeting_style, email_closing_style')
        .eq('id', user.id)
        .single();

      if (userError) throw userError;
      if (!userData) throw new Error('User data not found');

      const { data: blogSettings } = await supabase
        .from('user_blog_settings')
        .select('accent_color')
        .eq('user_id', user.id)
        .maybeSingle();

      const accentColor = blogSettings?.accent_color || '#FF6B35';

      const urlConfig = await getBlogUrlConfigForUser(user.id, supabase);
      // Eén bron van waarheid: de helper kiest zelf de eind-url (WordPress →
      // joepa → eigen domein → lees-nu) en filtert een lokale test-WordPress eruit.
      const blogUrlFor = (blog: { slug: string; wp_post_url: string | null }) =>
        user.widget_enabled
          ? generateWidgetBlogUrl(blog.slug, urlConfig, blog.wp_post_url)
          : generateBlogUrl(blog.slug, urlConfig, blog.wp_post_url);

      const blogsOverview = blogs
        .map((blog, index) => `${index + 1}. "${blog.title}" - ${blog.intro}`)
        .join('\n');

      const { data: { session } } = await supabase.auth.getSession();
      const authToken = session?.access_token || (import.meta.env.VITE_SUPABASE_ANON_KEY);

      updateStepStatus(1, 'completed');
      updateStepStatus(2, 'in_progress');

      console.log('[Multi-Blog Campaign] Calling generate function...');

      let lastError: Error | null = null;
      let data: { data?: string | EmailVariantResult } | null = null;

      for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
        try {
          setRetryAttempt(attempt);

          if (attempt > 1) {
            setProgressSteps(prev => prev.map((step, idx) =>
              idx === 2 ? { ...step, label: `AI voorwoord genereren (poging ${attempt}/${MAX_RETRY_ATTEMPTS})` } : step
            ));
          }

          console.log(`[Multi-Blog Campaign] Attempt ${attempt}/${MAX_RETRY_ATTEMPTS}`);

          const response = await fetchWithTimeout(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/openai-generate`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                promptType: 'email_variant_multi_blog',
                user_id: user.id,
                variables: {
                  blog_count: String(blogs.length),
                  blogs_overview: blogsOverview,
                  company_name: userData.company_name,
                  website_url: userData.website_url || '',
                  first_name: userData.first_name,
                },
              }),
              timeout: CAMPAIGN_GENERATION_TIMEOUT,
            }
          );

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Unknown error', category: 'UNKNOWN_ERROR' }));
            console.error(`[Multi-Blog Campaign] API Error on attempt ${attempt}:`, errorData);

            const category = errorData.category || 'UNKNOWN_ERROR';
            setErrorCategory(category);

            if (category === 'OPENAI_UNAVAILABLE' || category === 'OPENAI_SERVER_ERROR') {
              throw new Error('De AI service is momenteel niet beschikbaar. Dit is een tijdelijk probleem bij de provider. Probeer het over 5-10 minuten opnieuw.');
            } else if (category === 'OPENAI_RATE_LIMIT') {
              throw new Error('Te veel verzoeken naar de AI service. Wacht een paar minuten en probeer het opnieuw.');
            } else if (category === 'OPENAI_AUTH_ERROR') {
              throw new Error('Er is een authenticatie probleem met de AI service. Neem contact op met support.');
            }

            throw new Error(errorData.error || `Failed to generate email (${response.status})`);
          }

          data = await response.json();
          console.log(`[Multi-Blog Campaign] Success on attempt ${attempt}`);
          lastError = null;
          break;
        } catch (err) {
          lastError = err as Error;
          console.error(`[Multi-Blog Campaign] Error on attempt ${attempt}:`, err);

          const errorMessage = err instanceof Error ? err.message : String(err);
          const isTimeout = errorMessage.includes('Timeout') || (err instanceof Error && err.name === 'AbortError');
          const isNetwork = errorMessage.includes('fetch') || errorMessage.includes('network');

          await logGenerationError({
            generationType: 'campaign_variant',
            errorCategory: isTimeout ? 'TIMEOUT' : isNetwork ? 'NETWORK_ERROR' : 'API_ERROR',
            errorMessage: errorMessage,
            technicalError: err instanceof Error ? err.stack : undefined,
            retryAttempt: attempt - 1,
            blogId: blogIds[0],
            additionalContext: {
              service: 'openai',
              apiType: 'campaign_variant_generation',
              timeout: CAMPAIGN_GENERATION_TIMEOUT,
              lastError: errorMessage,
              maxRetries: MAX_RETRY_ATTEMPTS,
              attemptNumber: attempt,
              variantStyle: 'multi_blog',
              blogIds,
            },
          });

          if (attempt < MAX_RETRY_ATTEMPTS) {
            console.log(`[Multi-Blog Campaign] Waiting ${RETRY_DELAY}ms before retry...`);
            await sleep(RETRY_DELAY);
          }
        }
      }

      if (lastError) {
        updateStepStatus(2, 'error');
        throw lastError;
      }

      if (!data) {
        updateStepStatus(2, 'error');
        throw new Error('Geen resultaat ontvangen van AI');
      }

      setRetryAttempt(0);
      setProgressSteps(prev => prev.map((step, idx) =>
        idx === 2 ? { ...step, label: 'AI voorwoord genereren' } : step
      ));
      updateStepStatus(2, 'completed');
      updateStepStatus(3, 'in_progress');

      // Zelfde tolerante parsing als de single-blog varianten: modellen escapen
      // quotes in de HTML niet altijd correct binnen de JSON-string.
      const result: EmailVariantResult =
        typeof data.data === 'string' ? parseEmailVariant(data.data) : (data.data ?? {});

      if (!isUsableEmailVariant(result)) {
        updateStepStatus(2, 'error');
        throw new Error('De AI-tekst kon niet worden verwerkt. Probeer het opnieuw.');
      }

      const greeting = userData.email_greeting_style || '<p>Beste {{first_name|lezer}}</p>';
      const closing = userData.email_closing_style || '<p>Met vriendelijke groet,<br>{{first_name}}<br>{{company_name}}</p>';

      const emailImageUrls = await Promise.all(
        blogs.map((blog) => resolveEmailImageUrl(blog.featured_image_url))
      );

      const teasersHtml = blogs
        .map((blog, index) =>
          generateBlogTeaserHtml({
            title: blog.title,
            intro: blog.intro,
            imageUrl: emailImageUrls[index],
            buttonText: 'Lees het volledige artikel',
            buttonUrl: blogUrlFor(blog),
            accentColor: accentColor,
          })
        )
        .join('\n');

      const finalContent = `
        ${greeting}
        ${result.body_before_teaser || ''}
        ${teasersHtml}
        ${result.body_after_teaser || ''}
        ${closing}
      `;

      const { data: campaign, error: campaignError } = await supabase
        .from('email_campaigns')
        .insert({
          user_id: user.id,
          blog_id: null,
          subject: result.subject || 'Onze nieuwste blogs',
          content: finalContent,
          content_variant: 'multi_blog',
          from_name: userData.sendgrid_from_name || userData.first_name,
          status: 'draft',
        })
        .select()
        .single();

      if (campaignError) {
        updateStepStatus(3, 'error');
        throw campaignError;
      }

      updateStepStatus(3, 'completed');

      console.log('[Multi-Blog Campaign] Campaign created successfully:', campaign.id);
      navigate(`/campaigns/edit/${campaign.id}`);
    } catch (err) {
      console.error('[Multi-Blog Campaign] Error generating campaign:', err);

      let errorMessage = 'Er ging iets mis bij het genereren van de nieuwsbrief';

      if (err instanceof Error) {
        if (err.name === 'AbortError') {
          errorMessage = 'De aanvraag duurde te lang (timeout na 60 seconden). Probeer het opnieuw.';
          setErrorCategory('TIMEOUT');
        } else if (err.message.includes('NetworkError') || err.message.includes('Failed to fetch')) {
          errorMessage = 'Netwerkfout: Kan de AI-service niet bereiken. Controleer je internetverbinding en probeer het opnieuw.';
          setErrorCategory('NETWORK_ERROR');
        } else {
          errorMessage = err.message;
        }
      }

      setError(errorMessage);
      setRetryAttempt(0);

      const isTimeout = err instanceof Error && err.name === 'AbortError';
      const isNetwork = err instanceof Error && (err.message.includes('NetworkError') || err.message.includes('Failed to fetch'));

      await logGenerationError({
        generationType: 'campaign_variant',
        errorCategory: isTimeout ? 'TIMEOUT' : isNetwork ? 'NETWORK_ERROR' : 'UNKNOWN',
        errorMessage: errorMessage,
        technicalError: err instanceof Error ? err.stack : undefined,
        retryAttempt: retryAttempt,
        blogId: blogIds[0],
        additionalContext: {
          service: 'openai',
          apiType: 'campaign_variant_generation',
          timeout: CAMPAIGN_GENERATION_TIMEOUT,
          lastError: err instanceof Error ? err.message : String(err),
          variantStyle: 'multi_blog',
          blogIds,
        },
      });
    }
  };

  const handleRetry = () => {
    setError(null);
    setRetryAttempt(0);
    setProgressSteps([
      { label: 'Blogs ophalen', status: 'pending' },
      { label: 'Voorwoord voorbereiden', status: 'pending' },
      { label: 'AI voorwoord genereren', status: 'pending' },
      { label: 'Campaign opslaan', status: 'pending' },
    ]);
    generateCampaign();
  };

  const shouldShowRetryButton = errorCategory !== 'OPENAI_UNAVAILABLE' && errorCategory !== 'OPENAI_SERVER_ERROR';

  if (error) {
    return (
      <>
        <FocusHeader
          title="Nieuwsbrief genereren"
          onBack={() => navigate('/campaigns/create/multi-blog')}
        />
        <main className="flex-1 overflow-y-auto bg-[#F2F2EF] p-6">
          <div className="space-y-6 max-w-4xl mx-auto">
            <div className="card bg-red-50 border-red-200">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                    <AlertCircle className="w-6 h-6 text-red-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-[#231F20] mb-2">Nieuwsbrief Generatie Mislukt</h2>
                  <p className="text-gray-700 mb-4">{error}</p>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <h3 className="font-semibold text-blue-900 mb-2">Wat kun je doen?</h3>
                    <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                      <li>Controleer je internetverbinding</li>
                      <li>Probeer het over een paar minuten opnieuw</li>
                      <li>Kies een andere selectie van blogs</li>
                      <li>Maak een handmatige campaign aan</li>
                    </ul>
                  </div>

                  <div className="flex gap-3">
                    {shouldShowRetryButton && (
                      <button
                        onClick={handleRetry}
                        className="btn-primary"
                      >
                        Opnieuw Proberen
                      </button>
                    )}
                    <button
                      onClick={() => navigate('/campaigns/create/multi-blog')}
                      className="btn-secondary"
                    >
                      Terug naar Blogselectie
                    </button>
                    <button
                      onClick={() => navigate('/campaigns')}
                      className="px-6 py-2 text-gray-700 hover:text-gray-900 font-medium"
                    >
                      Naar Campaigns
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <FocusHeader
        title="Nieuwsbrief genereren"
        subtitle="Een moment geduld"
        onBack={() => navigate('/campaigns/create/multi-blog')}
      />
      <main className="flex-1 overflow-y-auto bg-[#F2F2EF] p-6">
        <div className="space-y-6 max-w-4xl mx-auto">
          <StepProgressIndicator steps={progressSteps} />
        </div>
      </main>
    </>
  );
}
